import type { FieldDto } from "../../types/field";
import type {
  FormBuilderDraft,
  FormBuilderField,
} from "../../types/formBuilder";
import type { GeneratedFormDraft } from "../../types/generateFields";
import {
  createEmptyDraft,
  createEmptySection,
  fieldFromDto,
  generateUniqueFieldName,
} from "./draft";
import { isKnownFieldType, toApiFieldType } from "./apiFieldTypes";

function generatedFieldToDto(
  raw: GeneratedFormDraft["fields"][number],
  index: number,
  usedNames: Set<string>,
): FieldDto {
  const type = toApiFieldType(String(raw.type ?? "text"));
  let name = String(raw.name ?? "").trim() || `field_${index + 1}`;
  if (usedNames.has(name)) {
    name = generateUniqueFieldName(usedNames, name);
  }
  usedNames.add(name);

  return {
    form_id: 0,
    order: raw.order ?? index,
    label: String(raw.label ?? "").trim(),
    name,
    type: isKnownFieldType(type) ? type : "text",
    required: Boolean(raw.required),
    options: (raw.options as FieldDto["options"]) ?? null,
    visibility: raw.visibility ?? null,
    select: raw.select ?? null,
    date: raw.date ?? null,
    dateRange: raw.dateRange ?? null,
    birthdate: raw.birthdate ?? null,
    city: raw.city ?? null,
    fio: raw.fio ?? null,
    display: raw.display ?? null,
  };
}

/**
 * Черновик AI → FormBuilderDraft без id полей.
 * Сохраняет структуру разделов, если она есть в ответе генератора.
 */
export function applyGeneratedFieldsToDraft(
  current: FormBuilderDraft,
  generated: GeneratedFormDraft,
  mode: "replace" | "append" = "replace",
): FormBuilderDraft {
  const usedNames = new Set<string>();
  if (mode === "append") {
    for (const section of current.sections) {
      for (const field of section.fields) {
        const name = field.name.trim();
        if (name) usedNames.add(name);
      }
    }
  }

  const toBuilderFields = (
    fields: GeneratedFormDraft["fields"],
  ): FormBuilderField[] =>
    fields.map((field, index) => {
      const dto = generatedFieldToDto(field, index, usedNames);
      const builderField = fieldFromDto(dto, index);
      // Новое поле не должно унаследовать ID уже сохранённого поля.
      delete builderField.id;
      return { ...builderField, order: index };
    });

  let nextSections: FormBuilderDraft["sections"];
  if (generated.sections?.length) {
    const baseOrder = mode === "append" ? current.sections.length : 0;
    const generatedSections = [...generated.sections]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((section, index) => ({
        ...createEmptySection(baseOrder + index),
        label: section.label.trim() || `Раздел ${baseOrder + index + 1}`,
        fields: toBuilderFields(section.fields),
      }));
    nextSections =
      mode === "append"
        ? [...current.sections, ...generatedSections]
        : generatedSections;
  } else {
    const builderFields = toBuilderFields(generated.fields);
    const sections =
      current.sections.length > 0 ? current.sections : [createEmptySection(0)];
    nextSections = sections.map((section, sectionIndex) => {
      if (sectionIndex !== 0) return section;

      const fields =
        mode === "append"
          ? [...section.fields, ...builderFields].map((field, index) => ({
              ...field,
              order: index,
            }))
          : builderFields;

      return { ...section, fields };
    });
  }

  const nextName =
    generated.name.trim() || current.name.trim() || "Новая форма";
  const nextDescription =
    generated.description?.trim() ||
    (mode === "replace" ? "" : current.description);

  return {
    ...current,
    name: mode === "replace" || !current.name.trim() ? nextName : current.name,
    description:
      mode === "replace" || !current.description.trim()
        ? nextDescription
        : current.description,
    sections: nextSections,
  };
}

/** Новый черновик только из ответа AI. */
export function draftFromGeneratedFields(
  generated: GeneratedFormDraft,
): FormBuilderDraft {
  return applyGeneratedFieldsToDraft(createEmptyDraft(), generated, "replace");
}
